// DAAM Admin — Social Integrations preview (Instagram + Snapchat).
// Admin-only. All controls disabled until backend flags are flipped.
// Never renders or stores any access_token.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Instagram, Ghost, ShieldAlert, Send, Upload } from "lucide-react";
import { api } from "../../lib/api";

function ProviderCard({ label, icon: Icon, provider, statusPath,
                        authorizePath, disconnectPath, refreshPath,
                        probePath }) {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState(null);       // last probe response
  const [probing, setProbing] = useState(false);

  const load = useCallback(async () => {
    try { setStatus((await api.get(statusPath)).data); }
    catch (e) {
      const detail = e.response?.data?.detail || {};
      setErr(detail.message_ar || detail.message_en || e.message || "status unavailable");
    }
  }, [statusPath]);
  useEffect(() => { load(); }, [load]);

  async function connect() {
    setBusy(true); setErr(null);
    try {
      const r = (await api.get(authorizePath)).data;
      window.location.assign(r.authorize_url);
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      setErr(detail.message_ar || detail.message_en || e.message);
    } finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true);
    try { await api.delete(disconnectPath); await load(); toast.info("تم فصل الحساب"); }
    catch (e) {
      const detail = e.response?.data?.detail || {};
      toast.error(detail.message_ar || detail.message_en || e.message);
    }
    finally { setBusy(false); }
  }
  async function refresh() {
    setBusy(true);
    try { await api.post(refreshPath); await load(); toast.success("تم تحديث الرمز"); }
    catch (e) {
      const detail = e.response?.data?.detail || {};
      toast.error(detail.message_ar || detail.message_en || e.message);
    } finally { setBusy(false); }
  }
  // Arabic outcome mapper for the Snapchat probe. Covers the four
  // required verdicts: ready / allowlist_required / token_expired /
  // fallback. Never surfaces any raw token, id, or Snap body.
  function snapchatProbeToast(data) {
    if (data.ready && data.status === "ready") {
      return { kind: "success", msg: "الاتصال جاهز" };
    }
    if (data.status === "allowlist_required") {
      return {
        kind: "error",
        msg: "يحتاج اعتماد Public Profile API من Snapchat",
      };
    }
    if (data.status === "token_expired") {
      return { kind: "error", msg: "انتهت صلاحية الرمز" };
    }
    return { kind: "error", msg: "تعذّر التحقق" };
  }
  async function runProbe() {
    // Live READ-ONLY health check. Uses the same axios instance so the
    // Bearer / cookie session is attached automatically. NEVER stores or
    // logs a token — the backend returns a whitelisted summary only.
    setProbing(true); setProbe(null);
    try {
      const data = (await api.get(probePath)).data;
      setProbe(data);
      if (provider === "snapchat") {
        const t = snapchatProbeToast(data);
        if (t.kind === "success") toast.success(t.msg);
        else                      toast.error(t.msg);
      } else if (data.live_ok && data.http_status === 200) {
        toast.success("الاتصال حي وجاهز");
      } else {
        toast.error("الاتصال غير جاهز — راجع التفاصيل");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      const msg = detail.message_ar || detail.message_en || e.message
        || (provider === "snapchat" ? "تعذّر التحقق" : "تعذّر تشغيل الفحص");
      setProbe(provider === "snapchat"
        ? { ready: false, status: "request_failed",
            http_status: e.response?.status || null }
        : { live_ok: false, http_status: e.response?.status || null,
            reason: "request_failed" });
      toast.error(msg);
    } finally { setProbing(false); }
  }

  const disabled = !status || status.disabled;
  return (
    <section data-testid={`social-card-${provider}`} dir="rtl"
             className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
      <header className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#BC944A]" />
        <h2 className="text-lg font-bold text-neutral-900">{label}</h2>
        {disabled && (
          <span data-testid={`${provider}-flag`}
                className="ms-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800">
            معطّل
          </span>
        )}
      </header>
      {status?.connected ? (
        <div className="space-y-2 text-sm">
          <div>الحالة: <b className="text-emerald-700">متّصل</b></div>
          {status.username && <div>@{status.username}</div>}
          {status.account_type && <div>النوع: {status.account_type}</div>}
          {status.expires_at && (
            <div className="text-xs text-neutral-500">
              انتهاء الرمز: {String(status.expires_at).slice(0, 19)}
            </div>
          )}
          <div className="flex gap-2 pt-2 flex-wrap">
            {refreshPath && (
              <button data-testid={`${provider}-refresh-btn`} onClick={refresh}
                      disabled={busy || disabled}
                      className="text-xs px-3 py-1.5 rounded-full border border-neutral-300 hover:bg-neutral-50 disabled:opacity-40">
                تحديث الرمز
              </button>
            )}
            {probePath && (
              <button data-testid={`${provider}-probe-btn`} onClick={runProbe}
                      disabled={probing || disabled}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#BC944A] text-[#BC944A] hover:bg-[#BC944A]/5 disabled:opacity-40">
                {probing ? "جارٍ الفحص…" : "فحص الاتصال الحي"}
              </button>
            )}
            <button data-testid={`${provider}-disconnect-btn`} onClick={disconnect}
                    disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100">
              فصل الحساب
            </button>
          </div>
          {probe && (
            <div data-testid={`${provider}-probe-result`}
                 className={`mt-2 rounded-xl border p-3 text-xs ${
                   (provider === "snapchat" ? probe.ready
                                             : probe.live_ok && probe.http_status === 200)
                     ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                     : "border-red-200 bg-red-50 text-red-800"}`}>
              <div className="flex items-center gap-2 mb-2">
                <b>الحكم:</b>
                <span data-testid={`${provider}-probe-verdict`}
                      className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                        (provider === "snapchat" ? probe.ready
                                                 : probe.live_ok && probe.http_status === 200)
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800"}`}>
                  {provider === "snapchat"
                    ? (probe.ready ? "READY" : `NOT_READY: ${probe.status || "unknown"}`)
                    : (probe.live_ok && probe.http_status === 200
                        ? "READY"
                        : `NOT_READY${probe.reason ? `: ${probe.reason}` : ""}`)}
                </span>
              </div>
              {provider === "snapchat" ? (
                <ul className="space-y-0.5">
                  <li>status: <b>{probe.status ?? "—"}</b></li>
                  <li>http_status: <b>{probe.http_status ?? "—"}</b></li>
                  <li>account_type: <b>{probe.account_type ?? "—"}</b></li>
                  <li>profile_id: <b>{probe.profile_id_masked ?? "—"}</b></li>
                  <li>checked_at: <b>{probe.checked_at
                    ? String(probe.checked_at).slice(0, 19) : "—"}</b></li>
                </ul>
              ) : (
                <ul className="space-y-0.5">
                  <li>live_ok: <b>{String(probe.live_ok)}</b></li>
                  <li>http_status: <b>{probe.http_status ?? "—"}</b></li>
                  <li>username: <b>{probe.username ?? "—"}</b></li>
                  <li>account_type: <b>{probe.account_type ?? "—"}</b></li>
                  <li>media_count: <b>{probe.media_count ?? "—"}</b></li>
                </ul>
              )}
            </div>
          )}
        </div>
      ) : (
        <button data-testid={`${provider}-connect-btn`} onClick={connect}
                disabled={busy || disabled}
                className={`w-full px-6 py-2.5 rounded-full text-sm font-semibold transition ${
                  disabled ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                           : "bg-[#BC944A] text-white hover:bg-[#a58038]"}`}>
          ربط الحساب
        </button>
      )}
      {disabled && (
        <div data-testid={`${provider}-disabled-banner`}
             className="rounded-xl border border-red-200 bg-red-50 text-red-800 p-3 text-xs flex gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5" />
          <span>
            هذا التكامل معطّل. المتغيّر <code>{provider.toUpperCase()}_INTEGRATION_STATUS</code> يجب أن يُضبط على <code>admin_only</code>.
          </span>
        </div>
      )}
      {err && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 p-3 text-xs">
          {err}
        </div>
      )}
    </section>
  );
}

export default function SocialIntegrationsPage() {
  const [igStatus, setIgStatus] = useState(null);

  // Poll Instagram status here as well so we can conditionally render the
  // test-publish panel only for the locked getdaam.sa BUSINESS connection.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = (await api.get("/instagram/status")).data;
        if (!cancel) setIgStatus(r);
      } catch { /* silent — the ProviderCard surfaces its own error */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Surface the outcome of an OAuth callback:
  //   ?ig=connected                     → success toast
  //   ?ig=error&code=<x>&msg=<ar>       → error toast
  //   ?snap=connected                   → success toast
  //   ?snap=error&code=<x>&msg=<ar>     → error toast
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ig = q.get("ig");
    if (ig === "connected") {
      toast.success("تم ربط حساب Instagram بنجاح");
    } else if (ig === "error") {
      const msg = q.get("msg");
      toast.error(msg ? decodeURIComponent(msg) : "فشل ربط Instagram");
    }
    const snap = q.get("snap");
    if (snap === "connected") {
      toast.success("تم ربط حساب Snapchat بنجاح");
    } else if (snap === "error") {
      const msg = q.get("msg");
      toast.error(msg ? decodeURIComponent(msg) : "فشل ربط Snapchat");
    }
    if (ig || snap) {
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const testPublishAllowed = !!(
    igStatus?.connected
    && igStatus?.username === "getdaam.sa"
    && ["BUSINESS", "CREATOR"].includes(
      (igStatus?.account_type || "").toUpperCase()
    )
  );

  return (
    <div dir="rtl" className="mx-auto max-w-3xl px-4 py-8 space-y-6" data-testid="social-page">
      <h1 className="text-2xl font-bold text-neutral-900">تكاملات التواصل الاجتماعي</h1>
      <p className="text-sm text-neutral-600">
        صفحة اختبار داخلية للمشرف. لا يظهر أي رمز وصول في المتصفح، ولا يتم النشر التلقائي في هذه المرحلة.
      </p>
      <ProviderCard label="Instagram Professional" icon={Instagram} provider="instagram"
                    statusPath="/instagram/status"
                    authorizePath="/instagram/authorize"
                    disconnectPath="/instagram/disconnect"
                    refreshPath="/instagram/refresh"
                    probePath="/instagram/probe" />
      {testPublishAllowed && <InstagramTestPublish />}
      <ProviderCard label="Snapchat" icon={Ghost} provider="snapchat"
                    statusPath="/snapchat/status"
                    authorizePath="/snapchat/authorize"
                    disconnectPath="/snapchat/disconnect"
                    refreshPath="/snapchat/refresh"
                    probePath="/snapchat/probe" />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Instagram Test-Publish panel (admin, locked to @getdaam.sa)
// ----------------------------------------------------------------------------
// Two-step flow to guard against accidental publishing:
//   1) User picks a JPG/PNG + caption → clicks "تجهيز المعاينة" which uploads
//      the image and returns a signed public HTTPS URL for Meta.
//   2) User reviews the full preview → clicks "نشر الآن" → a modal asks for
//      final confirmation (with the exact caption + image) → on confirm we
//      call /test-publish/publish with a client-generated idempotency key.
// The key is generated ONCE per upload and reused across retries so a
// double-click can never publish twice.
function InstagramTestPublish() {
  const [file, setFile]         = useState(null);
  const [caption, setCaption]   = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);     // local blob URL
  const [uploaded, setUploaded] = useState(null);         // {media_url, size, ...}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult]     = useState(null);         // publish response
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const idemKeyRef = useRef(null);
  const inputRef   = useRef(null);

  // Generate an idempotency key ONCE per prepared upload.
  const ensureIdemKey = () => {
    if (!idemKeyRef.current) {
      idemKeyRef.current =
        (crypto?.randomUUID?.() ?? String(Date.now()) + Math.random())
          .replace(/-/g, "")
          .slice(0, 32);
    }
    return idemKeyRef.current;
  };

  const captionLen = caption.length;
  const captionMax = 2200;
  const canPrepare = !!file && !uploading && captionLen <= captionMax;

  function onFileChange(e) {
    const f = e.target.files?.[0] || null;
    if (f && !["image/jpeg", "image/png"].includes(f.type)) {
      toast.error("الرجاء اختيار صورة JPG أو PNG فقط");
      e.target.value = "";
      return;
    }
    if (f && f.size > 8 * 1024 * 1024) {
      toast.error("حجم الصورة أكبر من 8 ميغابايت");
      e.target.value = "";
      return;
    }
    setFile(f);
    setUploaded(null);
    setResult(null);
    idemKeyRef.current = null;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
  }

  async function preparePreview() {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = (await api.post("/instagram/test-publish/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
      setUploaded(r);
      toast.success("تم رفع الصورة وتجهيز المعاينة");
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      toast.error(detail.message_ar || detail.message_en || e.message
                  || "فشل رفع الصورة");
    } finally { setUploading(false); }
  }

  async function doPublish() {
    if (!uploaded) return;
    setPublishing(true);
    try {
      const r = (await api.post("/instagram/test-publish/publish", {
        media_url:       uploaded.media_url,
        caption,
        idempotency_key: ensureIdemKey(),
      })).data;
      setResult(r);
      setConfirmOpen(false);
      toast.success("تم النشر بنجاح");
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      toast.error(detail.message_ar || detail.message_en
                  || e.message || "فشل النشر");
    } finally { setPublishing(false); }
  }

  const publishedAtLocal = useMemo(() => {
    if (!result?.published_at) return null;
    try { return new Date(result.published_at).toLocaleString("ar-SA"); }
    catch { return result.published_at; }
  }, [result]);

  return (
    <section data-testid="ig-test-publish-card" dir="rtl"
             className="rounded-2xl border border-[#BC944A]/40 bg-white p-5 space-y-4">
      <header className="flex items-center gap-2">
        <Send className="w-5 h-5 text-[#BC944A]" />
        <h2 className="text-lg font-bold text-neutral-900">
          اختبار نشر Instagram
        </h2>
        <span className="ms-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#BC944A]/10 text-[#BC944A]">
          @getdaam.sa فقط
        </span>
      </header>

      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-neutral-700 font-medium">صورة (JPG أو PNG، حتى 8 ميغابايت)</span>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png"
                 onChange={onFileChange}
                 data-testid="ig-tp-file-input"
                 className="mt-2 block w-full text-sm file:mr-3 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:bg-[#BC944A]/10 file:text-[#BC944A]
                            hover:file:bg-[#BC944A]/20" />
        </label>

        <label className="block text-sm">
          <span className="text-neutral-700 font-medium">الوصف</span>
          <textarea value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    maxLength={captionMax}
                    rows={4}
                    data-testid="ig-tp-caption"
                    placeholder="اكتب وصف المنشور هنا…"
                    className="mt-2 w-full rounded-xl border border-neutral-300 p-3 text-sm
                               focus:outline-none focus:ring-2 focus:ring-[#BC944A]/40" />
          <div className="text-[11px] text-neutral-500 mt-1 text-left tabular-nums">
            {captionLen} / {captionMax}
          </div>
        </label>

        {previewUrl && (
          <div data-testid="ig-tp-preview"
               className="rounded-xl border border-neutral-200 p-3 bg-neutral-50">
            <div className="text-xs font-semibold text-neutral-500 mb-2">معاينة</div>
            <div className="flex gap-3">
              <img src={previewUrl} alt="preview"
                   className="w-32 h-32 object-cover rounded-lg border border-neutral-200" />
              <div className="text-sm text-neutral-800 whitespace-pre-wrap break-words flex-1">
                {caption || <span className="text-neutral-400">— لا يوجد وصف —</span>}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <button data-testid="ig-tp-prepare-btn" onClick={preparePreview}
                  disabled={!canPrepare}
                  className="text-sm px-4 py-2 rounded-full bg-neutral-900 text-white
                             hover:bg-neutral-800 disabled:opacity-40 inline-flex items-center gap-2">
            <Upload className="w-4 h-4" />
            {uploading ? "جارٍ الرفع…" : "تجهيز المعاينة"}
          </button>
          <button data-testid="ig-tp-publish-btn"
                  onClick={() => setConfirmOpen(true)}
                  disabled={!uploaded || publishing}
                  className="text-sm px-4 py-2 rounded-full bg-[#BC944A] text-white
                             hover:bg-[#a58038] disabled:opacity-40 inline-flex items-center gap-2">
            <Send className="w-4 h-4" />
            نشر الآن
          </button>
        </div>
      </div>

      {result && (
        <div data-testid="ig-tp-result"
             className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-1">
          <div><b>الحالة:</b> {result.success ? "تم النشر" : "فشل"}</div>
          <div><b>media_id:</b> <code>{result.media_id}</code></div>
          {result.permalink && (
            <div>
              <b>الرابط: </b>
              <a href={result.permalink} target="_blank" rel="noreferrer"
                 className="text-[#BC944A] underline break-all">
                {result.permalink}
              </a>
            </div>
          )}
          {publishedAtLocal && (
            <div className="text-xs text-neutral-500">وقت النشر: {publishedAtLocal}</div>
          )}
          {result.cached && (
            <div className="text-[11px] text-neutral-500">
              (نتيجة مُخزَّنة سابقاً — لم يُعَد النشر)
            </div>
          )}
        </div>
      )}

      {/* Final confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
             data-testid="ig-tp-confirm-modal"
             onClick={(e) => e.target === e.currentTarget && setConfirmOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" dir="rtl">
            <h3 className="text-lg font-bold text-neutral-900">تأكيد النشر النهائي</h3>
            <p className="text-sm text-neutral-600">
              سيتم النشر مباشرة على حساب <b>@getdaam.sa</b>. هذا الإجراء لا يمكن التراجع عنه من هذه الصفحة.
            </p>
            {previewUrl && (
              <div className="flex gap-3 items-start bg-neutral-50 rounded-xl p-3">
                <img src={previewUrl} alt=""
                     className="w-20 h-20 object-cover rounded-lg" />
                <div className="text-xs text-neutral-800 whitespace-pre-wrap break-words flex-1">
                  {caption || "— لا يوجد وصف —"}
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <button data-testid="ig-tp-confirm-cancel"
                      onClick={() => setConfirmOpen(false)}
                      className="text-sm px-4 py-2 rounded-full border border-neutral-300
                                 hover:bg-neutral-50">
                إلغاء
              </button>
              <button data-testid="ig-tp-confirm-publish"
                      onClick={doPublish} disabled={publishing}
                      className="text-sm px-4 py-2 rounded-full bg-[#BC944A] text-white
                                 hover:bg-[#a58038] disabled:opacity-40">
                {publishing ? "جارٍ النشر…" : "تأكيد ونشر"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
